import { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { PageHero } from "@/components/shared/PageHero";
import { Reveal } from "@/components/shared/Reveal";
import { CTABand } from "@/components/shared/CTABand";
import { getProduct, PRODUCTS } from "@/data/products";
import { SERVICES } from "@/data/services";
import { INDUSTRIES } from "@/data/industries";
import { useSEO } from "@/lib/seo";

const Block = ({ label, children }) => (
  <div className="border-t border-line pt-6">
    <h2 className="font-mono-tech text-[11px] uppercase tracking-[0.25em] text-amber-brand mb-5">{label}</h2>
    {children}
  </div>
);

export default function ProductDetail() {
  const { slug } = useParams();
  const product = getProduct(slug);
  const [activeImg, setActiveImg] = useState(0);

  useSEO({
    title: product ? product.name : "Product",
    description: product ? product.short : "",
    jsonLd: product && {
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.name,
      description: product.intro,
      brand: { "@type": "Organization", name: "Vikas Rolls Private Limited" },
      image: window.location.origin + product.image,
    },
  });

  if (!product) return <Navigate to="/404" replace />;

  const related = SERVICES.filter((s) => product.relatedServices.includes(s.slug));
  const industryLinks = product.industries.map((name) => ({
    name,
    slug: INDUSTRIES.find((i) => i.name.toLowerCase().startsWith(name.toLowerCase().split(" ")[0]))?.slug,
  }));

  return (
    <>
      <PageHero
        chapter={product.index}
        label="Product"
        title={product.name}
        sub={product.short}
        crumbs={[{ label: "Products", to: "/products" }, { label: product.name }]}
      />

      <section className="bg-ink py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-[1fr_1.2fr] gap-14 items-start">
          <div className="lg:sticky lg:top-28">
            <Reveal>
              <div className="corner-marks border border-line overflow-hidden bg-ink-2">
                <img
                  src={product.gallery[activeImg]}
                  alt={`${product.name} — photograph ${activeImg + 1}`}
                  className="img-industrial w-full aspect-[4/3] object-cover"
                  data-testid="product-main-image"
                />
              </div>
              <div className="grid grid-cols-4 gap-2 mt-2">
                {product.gallery.map((img, i) => (
                  <button
                    key={img}
                    onClick={() => setActiveImg(i)}
                    data-testid={`product-gallery-thumb-${i}`}
                    aria-label={`View photograph ${i + 1} of ${product.name}`}
                    className={`border overflow-hidden ${i === activeImg ? "border-amber-brand" : "border-line hover:border-steel"}`}
                  >
                    <img src={img} alt="" className="w-full h-16 object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
              <div className="mt-6 border border-line bg-ink-2 p-5">
                <p className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-amber-brand mb-2">Specification note</p>
                <p className="text-sm text-steel leading-relaxed">{product.specNote}</p>
              </div>
            </Reveal>
          </div>

          <div className="space-y-12">
            <Reveal>
              <p className="text-steel-light text-lg md:text-xl leading-relaxed">{product.intro}</p>
            </Reveal>

            <Reveal delay={0.05}>
              <Block label="Typical Characteristics">
                <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                  {product.characteristics.map((c) => (
                    <li key={c} className="flex items-start gap-3 text-steel text-sm md:text-base">
                      <Check size={16} className="text-amber-brand flex-none mt-1" aria-hidden="true" />
                      {c}
                    </li>
                  ))}
                </ul>
              </Block>
            </Reveal>

            <Reveal delay={0.05}>
              <Block label="Applications">
                <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                  {product.applications.map((a) => (
                    <li key={a} className="flex items-start gap-3 text-steel text-sm md:text-base">
                      <span className="w-1.5 h-1.5 bg-amber-brand rotate-45 flex-none mt-2.5" aria-hidden="true" />
                      {a}
                    </li>
                  ))}
                </ul>
              </Block>
            </Reveal>

            <Reveal delay={0.05}>
              <Block label="Industries Commonly Served">
                <div className="flex flex-wrap gap-2.5">
                  {industryLinks.map(({ name, slug: iSlug }) =>
                    iSlug ? (
                      <Link key={name} to={`/industries/${iSlug}`} className="border border-line px-4 py-2 text-sm text-steel hover:border-amber-brand hover:text-amber-brand" data-testid={`product-industry-chip-${iSlug}`}>
                        {name}
                      </Link>
                    ) : (
                      <span key={name} className="border border-line px-4 py-2 text-sm text-steel">{name}</span>
                    )
                  )}
                </div>
              </Block>
            </Reveal>

            <Reveal delay={0.05}>
              <Block label="Available Customization">
                <div className="grid sm:grid-cols-2 gap-px bg-line border border-line">
                  {product.customization.map((c) => (
                    <div key={c} className="bg-ink-2 px-5 py-4 text-sm text-steel-light">{c}</div>
                  ))}
                </div>
              </Block>
            </Reveal>

            <Reveal delay={0.05}>
              <Block label="Related Technical Services">
                <div className="space-y-px bg-line border border-line">
                  {related.map((s) => (
                    <Link key={s.slug} to="/technical-services" className="group flex items-center justify-between bg-ink-2 px-5 py-4 hover:bg-ink-3" data-testid={`product-related-service-${s.slug}`}>
                      <span className="text-sm md:text-base text-steel-light group-hover:text-amber-brand">{s.name}</span>
                      <ArrowUpRight size={16} className="text-line group-hover:text-amber-brand" />
                    </Link>
                  ))}
                </div>
              </Block>
            </Reveal>

            <Reveal delay={0.05}>
              <Link
                to="/request-a-quote"
                data-testid="product-quote-button"
                className="group inline-flex items-center gap-3 bg-amber-brand text-ink font-display font-bold px-8 py-4 text-lg hover:bg-steel-light"
              >
                Request a Quote for {product.name}
                <ArrowRight size={19} className="transition-transform duration-300 group-hover:translate-x-1.5" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-ink-2 border-t border-line py-14">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <p className="font-mono-tech text-[11px] uppercase tracking-[0.25em] text-steel mb-6">Other products</p>
          <div className="flex flex-wrap gap-3">
            {PRODUCTS.filter((p) => p.slug !== slug).map((p) => (
              <Link key={p.slug} to={`/products/${p.slug}`} className="border border-line px-5 py-2.5 text-sm text-steel hover:border-amber-brand hover:text-amber-brand" data-testid={`product-other-link-${p.slug}`}>
                {p.name}
              </Link>
            ))}
          </div>
        </div>
      </section>
      <CTABand />
    </>
  );
}
